import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import Modal from '@/components/ui/Modal';
import Text from '@/components/ui/Text';
import { LinkButton } from '@/components/ui/Buttons';
import { GradientSpinner } from '@/components/ui/Spinner';
import { QTI_DOWNLOAD_RECORDED_EVENT } from '@/components/admin/QtiDownloadButton/constants';

interface PackageSubscriber {
  id: number;
  last_sent: string;
  name: string;
}

interface QtiPackageSubscribersCellProps {
  downloadsUrl: string;
  initialCount: number;
  packageId: number;
}

const ROW_CLASSES = [
  'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-4 tw-py-3',
  '[&:not(:last-child)]:tw-border-b [&:not(:last-child)]:tw-border-solid',
  '[&:not(:last-child)]:tw-border-light-grey',
].join(' ');

const SubscriberList = ({ subscribers }: { subscribers: PackageSubscriber[] }) => (
  <ul className="tw-list-none tw-p-0 tw-m-0 tw-border tw-border-solid tw-border-light-grey tw-rounded">
    {subscribers.map(subscriber => (
      <li
        key={subscriber.id}
        className={ROW_CLASSES}
      >
        <Text className="tw-text-tci-black" tag="span" weight="bold">{subscriber.name}</Text>
        <Text className="tw-text-med-dark-grey" size="sm" tag="span">
          Last sent {subscriber.last_sent}
        </Text>
      </li>
    ))}
  </ul>
);

const QtiPackageSubscribersCell = ({
  downloadsUrl,
  initialCount,
  packageId,
}: QtiPackageSubscribersCellProps) => {
  const [count, setCount] = useState(initialCount);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [subscribers, setSubscribers] = useState<PackageSubscriber[] | null>(null);

  const fetchSubscribers = () => (
    Axios
      .get<{ subscribers: PackageSubscriber[] }>(downloadsUrl)
      .then(({ data }) => {
        setFetchFailed(false);
        setSubscribers(data.subscribers);
        setCount(data.subscribers.length);
      })
  );

  useEffect(() => {
    const handleDownloadRecorded = (e: Event) => {
      const { detail } = e as CustomEvent<{ packageId: number, subscriberCount: number }>;
      if (detail?.packageId !== packageId) return;

      // AIDEV-NOTE: read the count from the event, don't refetch — the download navigation that
      // follows this event cancels in-flight XHRs in real browsers, silently dropping a refresh.
      setCount(detail.subscriberCount);
    };

    window.addEventListener(QTI_DOWNLOAD_RECORDED_EVENT, handleDownloadRecorded);
    return () => window.removeEventListener(QTI_DOWNLOAD_RECORDED_EVENT, handleDownloadRecorded);
  }, [packageId]);

  const loadSubscribers = () => {
    setFetchFailed(false);
    setSubscribers(null);
    fetchSubscribers().catch(() => setFetchFailed(true));
  };

  const openModal = () => {
    setIsOpen(true);
    loadSubscribers();
  };

  if (count === 0) return <span aria-label="Never downloaded">—</span>;

  return (
    <>
      <LinkButton onClick={openModal}>
        {count} {count === 1 ? 'subscriber' : 'subscribers'}
      </LinkButton>
      {isOpen && (
        <Modal isOpen close={() => setIsOpen(false)} headerText="Subscribers">
          {fetchFailed && (
            <div className="tw-flex tw-items-center tw-gap-2">
              <Text className="tw-text-red" size="sm" tag="span">
                Something went wrong loading subscribers.
              </Text>
              <LinkButton onClick={loadSubscribers}>Try again</LinkButton>
            </div>
          )}
          {!fetchFailed && subscribers === null && (
            <div className="tw-flex tw-justify-center tw-py-6">
              <GradientSpinner size={32} />
            </div>
          )}
          {subscribers !== null && (
            <>
              <Text className="tw-mb-4">
                This package has been sent to {count} {count === 1 ? 'subscriber' : 'subscribers'}
              </Text>
              <SubscriberList subscribers={subscribers} />
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default QtiPackageSubscribersCell;
